import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Не из индекса фичи: индекс тянет экран игры с движком. Здесь — только состояние и картинки.
import { ART } from '@features/London/assets/art';
import { formatPence, foxStatus, unreadChats, useLondonSummary } from '@features/London/model/summary';
import { trackEvent } from '@shared/analytics';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';

/**
 * Игра на главной — сцена во всю ширину под приветствием (владелец 2026-11-03 выбрал макет 4 «Игра сценой сверху»;
 * артефакт «Главная Lomora: четыре макета»). Фон один и тот же всегда (`home-bg`; пока художник рисует — кафе), меняется
 * только лисёнок по состоянию (`home-fox-*`: начинает, всё хорошо, сообщения, голоден, устал; пока — спрайт игры) и
 * его реплика. Слева поверх затемнения — что с ним, деньги, силы, сытость и пиксельная кнопка «Продолжить игру».
 */

type Mood = 'start' | 'happy' | 'messages' | 'hungry' | 'tired';

const BUBBLE: Record<Mood, string> = {
  start: 'В Лондон!',
  happy: 'Идём гулять?',
  messages: 'Мне написали!',
  hungry: 'Пора перекусить!',
  tired: 'Сил нет…',
};

function Meter({ value, label }: { value: number; label: string }) {
  const tone = value < 30 ? 'bg-[#e6453b]' : value < 60 ? 'bg-[#f2c14e]' : 'bg-[#4cc38a]';
  return (
    <span
      className="relative inline-block h-[6px] w-11 overflow-hidden rounded-full bg-white/15 align-middle"
      role="meter"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <i className={cn('absolute inset-y-0 left-0 block', tone)} style={{ width: `${Math.max(4, Math.min(100, value))}%` }} />
    </span>
  );
}

export function HomeGameBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const london = useLondonSummary();
  const denied = london.denied || london.failed;
  const state = denied ? null : london.state;
  const status = state ? foxStatus(state) : null;
  const fresh = !state || Boolean(status?.fresh);
  const mood: Mood = fresh
    ? 'start'
    : !state
      ? 'happy'
      : state.energy.left <= 0
        ? 'tired'
        : state.hunger < 30
          ? 'hungry'
          : unreadChats(state).length
            ? 'messages'
            : 'happy';

  // Лисёнок, вписанный в сцену (владелец 2026-11-03: «не поверх картинки, а в картинке») — одна картинка на состояние
  // с тем же фоном; пока художник рисует — фон и вырезанный лисёнок поверх.
  const scene = ART[`home-scene-${mood}`];
  const bg = scene ?? ART['home-bg'] ?? ART['dlg-cafe-wide'];
  const pose = scene ? undefined : ART[`home-fox-${mood}`];
  const sprite = ART.fox;
  const foxStyle: CSSProperties | undefined = scene
    ? undefined
    : pose
    ? { backgroundImage: `url(${pose.url})`, backgroundSize: 'contain', aspectRatio: `${pose.w} / ${pose.h}` }
    : sprite?.cell
      ? {
          backgroundImage: `url(${sprite.url})`,
          backgroundSize: `${(sprite.cols ?? 4) * 100}% ${(sprite.rows ?? 4) * 100}%`,
          backgroundPosition: '0 0',
          aspectRatio: `${sprite.cell[0]} / ${sprite.cell[1]}`,
        }
      : undefined;

  const open = () => {
    trackEvent('home_game_clicked', { action: denied ? 'stories' : fresh ? 'london_start' : 'london_resume', mission_done: null });
    navigate(denied ? paths.stories : paths.london);
  };

  const kicker =
    state && !fresh
      ? [
          t('dashboard.home.game.kicker'),
          state.chapter ? t('dashboard.home.game.chapter', { no: state.chapter.no }) : null,
          t('dashboard.home.game.day', { day: state.day }),
        ]
          .filter(Boolean)
          .join(' · ')
      : t('dashboard.home.game.kicker');
  const title = london.loading && !state ? t('dashboard.home.game.title') : (status?.title ?? t('dashboard.home.game.title'));

  return (
    <section aria-labelledby="home-game-title" className="home-banner relative overflow-hidden rounded-card border border-solid border-[#3a3a40] bg-[#1d2040]">
      {/* Сцена — для настроения; нажатие по ней тоже ведёт в игру. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={open}
        className="hb-bg absolute inset-0 block w-full"
        style={bg ? { backgroundImage: `url(${bg.url})` } : undefined}
      />
      {foxStyle ? <span aria-hidden className="hb-fox pointer-events-none absolute block bg-no-repeat" style={foxStyle} /> : null}
      {state || fresh ? (
        <span aria-hidden className={'hb-bubble pointer-events-none absolute' + (scene ? ' in-scene' : '')}>
          {BUBBLE[mood]}
        </span>
      ) : null}
      <span aria-hidden className="hb-shade pointer-events-none absolute inset-0" />
      <div className="hb-body pointer-events-none relative flex h-full flex-col justify-center">
        <div className="hb-kicker font-semibold uppercase text-accent">{kicker}</div>
        <h2 id="home-game-title" className="hb-title text-balance font-serif font-medium tracking-[-0.01em] text-fg">
          {title}
        </h2>
        {state && !fresh ? (
          <>
            <div className="hb-stats flex flex-wrap gap-1.5">
              <span className="hb-stat">{formatPence(state.money_pence)}</span>
              <span className="hb-stat">⚡ {state.energy.bonus ? state.energy.left : `${state.energy.left}/${state.energy.max}`}</span>
              <span className="hb-stat">
                {t('dashboard.home.game.hunger')} <Meter value={state.hunger} label={t('dashboard.home.game.hunger')} />
              </span>
            </div>
            <p className="hb-meta">
              {formatPence(state.money_pence)} · ⚡ {state.energy.left}/{state.energy.max}
            </p>
          </>
        ) : status ? (
          <p className="hb-text text-[#cfcac2]">{status.text}</p>
        ) : null}
        <button type="button" onClick={open} className="px-btn pointer-events-auto self-start">
          <span aria-hidden className="px-play" />
          <span className="hb-btn-long">{fresh ? t('dashboard.home.game.play') : t('dashboard.home.game.resume')}</span>
          <span className="hb-btn-short">{fresh ? t('dashboard.home.game.play') : t('dashboard.home.dailies.resume')}</span>
        </button>
      </div>
    </section>
  );
}
